<template>
    <div class="m-profile-page">
      <div class="sticky-header">
        <FeedHeader />
      </div>
  
      <main class="m-profile-content">
        <button class="back-btn" @click="$router.back()">
          ← Назад
        </button>
  
        <div v-if="isLoadingProfile" class="company-skeleton">
          <div class="skeleton title-skeleton"></div>
          <div class="skeleton card-skeleton"></div>
          <div class="skeleton button-skeleton"></div>
        </div>
  
        <section v-else class="profile-company-card">
          <h1 class="section-title">{{ titleText }}</h1>
  
          <div v-if="hasMultiCompanies" class="multi-company-wrapper">
            <MyCompany
              v-for="c in companyList"
              :key="c.company_id"
              :company="c"
              @delete="confirmDeleteCompany(c)"
            />
          </div>
  
          <div v-else-if="hasSingleCompany" class="single-company-wrapper">
            <MyCompany
              :company="currentCompany"
              @delete="confirmDeleteCompany(currentCompany)"
            />
          </div>
  
          <NoCompanyPlaceholder v-else @add="checkSubscriptionBeforeAdd" />
  
          <button
            class="add-button"
            @click="checkSubscriptionBeforeAdd"
            :disabled="isCheckingSubscription"
          >
            <img v-if="!isCheckingSubscription" src="../../assets/add-icon.png" alt="+" />
            <div v-else class="mini-btn-loader"></div>
            <span>Добавить компанию</span>
          </button>
  
          <div v-if="showSubscriptionHint" class="subscription-hint">
            Вам нужна подписка <strong>Fullstack HR</strong> для добавления нескольких компаний
          </div>
        </section>
      </main>
  
      <div v-if="isDeleting" class="loader-overlay">
        <div class="loader"></div>
      </div>
  
      <transition name="fade">
        <div
          v-if="showConfirm"
          class="confirm-overlay"
          :style="{ pointerEvents: backdropReady ? 'auto' : 'none' }"
          @click.self="cancelDelete"
        >
          <div class="confirm-dialog" @click.stop>
            <h3>Подтверждение удаления</h3>
            <p>
              Вы точно хотите разорвать связь с компанией
              <strong>{{ companyToDelete?.name }}</strong>?
            </p>
  
            <div class="buttons">
              <button class="btn cancel-btn" @click="cancelDelete">Отмена</button>
              <button class="btn delete-btn" @click="deleteCompany">Разорвать</button>
            </div>
          </div>
        </div>
      </transition>
  
      <transition name="fade">
        <div v-if="showSnackbar" class="snackbar">{{ snackbarMessage }}</div>
      </transition>
  
      <ProfileCompanyAnimationDrawer
        :visible="showCompanyDrawer"
        :isFullStackHRPremiumOpen="isPremiumModeForCompany"
        @close="showCompanyDrawer = false"
        @start-overlay="isCompanyUpdating = true"
        @stop-overlay="isCompanyUpdating = false"
        @company-added="onCompanyAdded"
      />
  
      <div v-if="isCompanyUpdating" class="company-overlay">
        <div class="spinner"></div>
      </div>
  
      <teleport to="body">
        <Paywall v-if="showPaywall" @close="showPaywall = false" />
      </teleport>
  
      <MobileTabBar />
    </div>
  </template>
  
  <script setup>
  import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
  import FeedHeader from '../FeedHeader.vue';
  import MobileTabBar from '../MobileTabBar2.vue';
  import MyCompany from '../profile/MyCompany.vue';
  import NoCompanyPlaceholder from '../profile/NoCompanyPlaceholder.vue';
  import ProfileCompanyAnimationDrawer from '../profile/ProfileCompanyAnimationDrawer.vue';
  import Paywall from '../../views/Paywall.vue';
  import UserService from '../../api/UserService.js';
  import PremiumService from '../../api/PremiumService.js';
  
  const userId = localStorage.getItem('user_id');
  const user = ref({});
  
  const isLoadingProfile = ref(true);
  const isDeleting = ref(false);
  const isCheckingSubscription = ref(false);
  const isCompanyUpdating = ref(false);
  const isPremiumModeForCompany = ref(false);
  
  const showConfirm = ref(false);
  const backdropReady = ref(false);
  const showSubscriptionHint = ref(false);
  const showSnackbar = ref(false);
  const snackbarMessage = ref('');
  const snackbarType = ref('success');
  const showCompanyDrawer = ref(false);
  const showPaywall = ref(false);
  
  const companyToDelete = ref(null);
  let hintTimeout = null;
  let snackbarTimer = null;
  
  const currentCompany = computed(() => user.value?.current_company || null);
  const companies = computed(() => Array.isArray(user.value?.companies) ? user.value.companies : []);
  
  const hasMultiCompanies = computed(() => companies.value.length > 0);
  
  const hasSingleCompany = computed(() => {
    return currentCompany.value && Object.keys(currentCompany.value).length > 0;
  });
  
  const companyList = computed(() => {
    if (hasMultiCompanies.value) return companies.value;
    if (hasSingleCompany.value) return [currentCompany.value];
    return [];
  });
  
  const titleText = computed(() => {
    const count = companyList.value.length;
  
    if (count > 1) return 'Компании, которые вы представляете';
    if (count === 1) return 'Компания, которую вы представляете';
    return 'Вы ещё не представили ни одной компании';
  });
  
  function showSnackbarMessage(message, type = 'success') {
    snackbarMessage.value = message;
    snackbarType.value = type;
    showSnackbar.value = true;
  
    clearTimeout(snackbarTimer);
    snackbarTimer = setTimeout(() => {
      showSnackbar.value = false;
    }, 3000);
  }
  
  function getLocalProfile() {
    try {
      return JSON.parse(localStorage.getItem('user_profile')) || {};
    } catch {
      return {};
    }
  }
  
  function setUser(profile) {
    user.value = profile || {};
    localStorage.setItem('user_profile', JSON.stringify(user.value));
  }
  
  async function loadProfile() {
    const startedAt = performance.now();
    isLoadingProfile.value = true;
  
    const cached = getLocalProfile();
  
    if (cached && Object.keys(cached).length) {
      user.value = cached;
    }
  
    try {
      const fetched = await UserService.getProfile(userId);
      setUser(fetched || {});
    } catch (error) {
      console.error('Ошибка при загрузке профиля:', error);
      showSnackbarMessage('Не удалось загрузить компании', 'error');
    } finally {
      const minSkeletonMs = 400;
      const elapsed = performance.now() - startedAt;
      const delay = Math.max(0, minSkeletonMs - elapsed);
  
      setTimeout(() => {
        isLoadingProfile.value = false;
      }, delay);
    }
  }
  
  function showHint() {
    showSubscriptionHint.value = true;
  
    clearTimeout(hintTimeout);
    hintTimeout = setTimeout(() => {
      showSubscriptionHint.value = false;
    }, 14000);
  }
  
  async function confirmDeleteCompany(c) {
    companyToDelete.value = c;
  
    showConfirm.value = false;
    await nextTick();
  
    setTimeout(() => {
      showConfirm.value = true;
      backdropReady.value = false;
  
      setTimeout(() => {
        backdropReady.value = true;
      }, 100);
    }, 0);
  }
  
  function cancelDelete() {
    if (!backdropReady.value) return;
    showConfirm.value = false;
  }
  
  function removeCompanyFromLocalState(companyId) {
    const updatedCompanies = companies.value.filter((c) => c.company_id !== companyId);
  
    user.value = {
      ...user.value,
      companies: updatedCompanies,
      current_company:
        user.value?.current_company?.company_id === companyId
          ? null
          : user.value?.current_company,
    };
  
    localStorage.setItem('user_profile', JSON.stringify(user.value));
  }
  
  async function deleteCompany() {
    if (!backdropReady.value) return;
    showConfirm.value = false;
  
    if (!companyToDelete.value) {
      showSnackbarMessage('Компания не выбрана', 'error');
      return;
    }
  
    if (!userId) {
      showSnackbarMessage('Пользователь не авторизован', 'error');
      return;
    }
  
    isDeleting.value = true;
  
    try {
      if (hasMultiCompanies.value) {
        await UserService.removeUserCompanyIfMultiples(companyToDelete.value.company_id);
      } else {
        await UserService.removeUserCompany(userId);
      }
  
      removeCompanyFromLocalState(companyToDelete.value.company_id);
      showSnackbarMessage('Компания успешно удалена');
    } catch (error) {
      console.error('Ошибка при удалении компании:', error);
      showSnackbarMessage('Ошибка при удалении компании', 'error');
    } finally {
      isDeleting.value = false;
      companyToDelete.value = null;
    }
  }
  
  function openCompanyDrawerPremium() {
    isPremiumModeForCompany.value = true;
    showCompanyDrawer.value = true;
  }
  
  function openPaywall() {
    showPaywall.value = false;
    nextTick(() => {
      showPaywall.value = true;
    });
  }
  
  async function checkSubscriptionBeforeAdd() {
    if (isCheckingSubscription.value) return;
  
    isCheckingSubscription.value = true;
  
    try {
      const sub = await PremiumService.fetchMySubscription();
  
      if (!sub || !sub.isActive || sub.planType !== 'premium_fullstack_hr') {
        showHint();
        openPaywall();
        return;
      }
  
      openCompanyDrawerPremium();
    } catch (error) {
      console.error('Ошибка проверки подписки:', error);
      showHint();
    } finally {
      isCheckingSubscription.value = false;
    }
  }
  
  function onCompanyAdded(fullCompany) {
    const oldCompanies = companies.value;
    const exists = oldCompanies.some((c) => c.company_id === fullCompany.company_id);
    const updatedCompanies = exists
      ? oldCompanies.map((c) => (c.company_id === fullCompany.company_id ? fullCompany : c))
      : [...oldCompanies, fullCompany];
  
    user.value = {
      ...user.value,
      current_company: fullCompany,
      companies: updatedCompanies,
    };
  
    localStorage.setItem('user_profile', JSON.stringify(user.value));
    showSnackbarMessage(`Компания обновлена: ${fullCompany.name}`);
  }
  
  onMounted(() => {
    loadProfile();
  });
  
  onBeforeUnmount(() => {
    clearTimeout(hintTimeout);
    clearTimeout(snackbarTimer);
  });
  </script>
  
  <style scoped>
  .m-profile-page {
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    background: var(--background-color);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  
  .sticky-header {
    flex-shrink: 0;
  }
  
  .m-profile-content {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 16px;
    padding-bottom: 96px;
    box-sizing: border-box;
    -webkit-overflow-scrolling: touch;
  }
  
  .back-btn {
    height: 36px;
    padding: 0 14px;
    margin-bottom: 14px;
    border: none;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: blur(8px);
    color: #111;
    font-size: 13px;
    font-family: 'Eastman-Medium', sans-serif;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
  }
  
  [data-theme='dark'] .back-btn {
    background: rgba(40, 40, 40, 0.92);
    color: #fff;
  }
  
  .profile-company-card {
    width: 100%;
    max-width: 430px;
    margin: 0 auto;
    padding: 20px;
    box-sizing: border-box;
    border-radius: 24px;
    background: var(--sidebar-background);
    border: 1px solid #e6e6e6;
    font-family: 'Eastman-Regular', sans-serif;
  }
  
  [data-theme='dark'] .profile-company-card {
    background: #2f2f2f;
    color: #eee;
    border-color: #3f3f3f;
  }
  
  .section-title {
    margin: 0 0 16px;
    font-size: 18px;
    line-height: 1.2;
    font-family: 'Eastman-Bold', sans-serif;
    letter-spacing: -0.4px;
    color: var(--text-primary);
  }
  
  .multi-company-wrapper,
  .single-company-wrapper {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  
  .add-button {
    width: 100%;
    min-height: 50px;
    margin-top: 18px;
    padding: 0 18px;
    border: none;
    border-radius: 999px;
    background: #2173e5;
    color: #fff;
    font-size: 15px;
    font-family: 'Eastman-Medium', sans-serif;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    cursor: pointer;
    box-shadow: 0 8px 24px rgba(33, 115, 229, 0.24);
  }
  
  .add-button:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    box-shadow: none;
  }
  
  .add-button img {
    width: 20px;
    height: 20px;
    filter: brightness(0) invert(1);
  }
  
  .subscription-hint {
    margin-top: 14px;
    padding: 12px 16px;
    background: #fff;
    color: #333;
    border-radius: 14px;
    font-size: 13px;
    line-height: 1.35;
    font-family: 'Eastman-Medium', sans-serif;
    border: 1px solid #d0d0d0;
    position: relative;
    animation: popIn 0.25s ease-out;
  }
  
  .subscription-hint::after {
    content: '';
    position: absolute;
    top: -8px;
    left: 28px;
    width: 14px;
    height: 14px;
    background: white;
    border-left: 1px solid #d0d0d0;
    border-top: 1px solid #d0d0d0;
    transform: rotate(45deg);
  }
  
  [data-theme='dark'] .subscription-hint {
    background: #2c2c2e;
    color: #eee;
    border-color: #555;
  }
  
  [data-theme='dark'] .subscription-hint::after {
    background: #2c2c2e;
    border-color: #555;
  }
  
  .confirm-overlay,
  .loader-overlay,
  .company-overlay {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .confirm-overlay {
    background: rgba(0, 0, 0, 0.5);
    z-index: 2000;
  }
  
  .confirm-dialog {
    width: calc(100vw - 32px);
    max-width: 380px;
    padding: 24px 20px;
    box-sizing: border-box;
    border-radius: 22px;
    background: white;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
    text-align: center;
    font-family: 'Eastman-Regular', sans-serif;
  }
  
  .confirm-dialog h3 {
    margin: 0 0 14px;
    font-size: 18px;
    font-family: 'Eastman-Bold', sans-serif;
  }
  
  .confirm-dialog p {
    margin: 0 0 22px;
    font-size: 14px;
    line-height: 1.45;
    color: #444;
  }
  
  .confirm-dialog strong {
    color: #2173e5;
  }
  
  [data-theme='dark'] .confirm-dialog {
    background: #2a2a2a;
    color: #eee;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.7);
  }
  
  [data-theme='dark'] .confirm-dialog p {
    color: #ccc;
  }
  
  .buttons {
    display: flex;
    gap: 12px;
  }
  
  .btn {
    flex: 1;
    height: 46px;
    border-radius: 14px;
    font-family: 'Eastman-Medium', sans-serif;
    font-size: 14px;
    cursor: pointer;
    border: none;
  }
  
  .cancel-btn {
    background: #ddd;
    color: #333;
  }
  
  .delete-btn {
    background: #e65122;
    color: white;
  }
  
  [data-theme='dark'] .cancel-btn {
    background: #555;
    color: #ddd;
  }
  
  [data-theme='dark'] .delete-btn {
    background: #bf3f15;
  }
  
  .loader-overlay {
    background: rgba(0, 0, 0, 0.3);
    z-index: 3000;
  }
  
  .company-overlay {
    background: rgba(0, 0, 0, 0.25);
    z-index: 9999;
    backdrop-filter: blur(3px);
  }
  
  .loader,
  .spinner,
  .mini-btn-loader {
    border-style: solid;
    border-top-color: transparent;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }
  
  .loader,
  .spinner {
    width: 42px;
    height: 42px;
    border-width: 4px;
    border-color: rgba(255, 255, 255, 0.45);
    border-top-color: #2173e5;
  }
  
  .mini-btn-loader {
    width: 18px;
    height: 18px;
    border-width: 3px;
    border-color: rgba(255, 255, 255, 0.45);
    border-top-color: #fff;
  }
  
  .snackbar {
    position: fixed;
    left: 50%;
    bottom: calc(86px + env(safe-area-inset-bottom));
    transform: translateX(-50%);
    max-width: calc(100vw - 32px);
    background: #2173e5;
    color: white;
    padding: 10px 18px;
    border-radius: 14px;
    font-family: 'Eastman-Medium', sans-serif;
    font-size: 13px;
    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.22);
    z-index: 110;
  }
  
  .company-skeleton {
    width: 100%;
    max-width: 430px;
    margin: 0 auto;
    padding: 20px;
    box-sizing: border-box;
    border-radius: 24px;
    background: var(--sidebar-background);
  }
  
  .skeleton {
    background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
    background-size: 200% 100%;
    animation: skeleton-shimmer 1.2s infinite;
    border-radius: 14px;
  }
  
  [data-theme='dark'] .skeleton {
    background: linear-gradient(90deg, #333 25%, #444 50%, #333 75%);
    background-size: 200% 100%;
  }
  
  .title-skeleton {
    width: 72%;
    height: 24px;
  }
  
  .card-skeleton {
    width: 100%;
    height: 160px;
    margin-top: 18px;
  }
  
  .button-skeleton {
    width: 100%;
    height: 50px;
    margin-top: 18px;
    border-radius: 999px;
  }
  
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.22s ease;
  }
  
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
  
  @keyframes popIn {
    from {
      opacity: 0;
      transform: scale(0.92) translateY(6px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
  
  @keyframes skeleton-shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
  
  @media (min-width: 768px) {
    .m-profile-page {
      max-width: 430px;
      margin: 0 auto;
      border-left: 1px solid var(--separator_home);
      border-right: 1px solid var(--separator_home);
    }
  }
  
  @media (max-width: 360px) {
    .m-profile-content {
      padding-left: 12px;
      padding-right: 12px;
    }
  
    .profile-company-card,
    .company-skeleton {
      padding: 16px;
    }
  
    .section-title {
      font-size: 17px;
    }
  }
  </style>
  